import React, { useState } from "react"
import Icon from "@kit/design/icon"
import SocialIcon from "@kit/design/social-icon"
import { useChildElements } from "../utils/use-child-elements"
import { useEditingContext, useElement } from "../data/form-data"
import { ContentEditable } from "./content-editable"
import { useBuilderState } from "app/forms/builder/builder-context"
import { SettingsButton } from "./settings-button"
import Input from "@kit/design/input"
import { VisuallyHidden } from "@kit/design/visually-hidden"
import { OldButton } from "@kit/design/old-button"
import { getLinkMeta } from "../utils/get-link-meta"
import { isEmpty } from "lodash"

import "./links-element.css"

const COLUMN_CLASSES = {
  1: "grid-cols-1",
  2: "md:grid-cols-2"
}

function LinkIcon({ icon, borderStyles, styles, classes }) {
  return (
    <div
      className={`flex p-4 ${classes} ${
        borderStyles.borderWidth !== "0" ? "mr-3" : ""
      }`}
      style={borderStyles}
    >
      <SocialIcon name={icon} style={styles} />
    </div>
  )
}

function Link({
  element_id,
  icon,
  hasIcon,
  settings: {
    backgroundColor,
    radius,
    shadow,
    textColor,
    iconColor,
    iconColorStyle,
    iconBorder,
    iconBorderColor,
    linkBorder,
    linkBorderColor
  }
}) {
  const [element] = useElement(element_id)
  const { url } = element
  const editing = useEditingContext()
  const { setActiveElement } = useBuilderState()
  const handleClick = () => setActiveElement(element_id)

  const onKeyPress = event => {
    if (event.key === "Enter") {
      handleClick()
    }
  }

  const styles = {
    backgroundColor: backgroundColor,
    borderRadius: radius,
    boxShadow: shadow,
    color: textColor,
    borderStyle: "solid",
    borderWidth: linkBorder,
    borderColor: linkBorderColor,
    minHeight: "83px"
  }

  const iconBorderStyles = {
    borderRadius: radius,
    borderStyle: "solid",
    borderWidth: iconBorder,
    borderColor: iconBorderColor
  }

  const iconStyles = {
    width: 25,
    height: 25
  }

  let classes = ""
  if (iconColorStyle === "custom") {
    iconStyles.color = iconColor
  } else {
    classes = `text-brand-${icon}`
  }

  if (editing) {
    return (
      <div className="relative">
        <div
          className={`cursor-pointer flex items-center p-3 h-full transition duration-300 focus:outline-blue ${
            hasIcon === "none" ? "px-6 justify-center text-center" : ""
          }`}
          style={styles}
          onKeyPress={onKeyPress}
          onClick={handleClick}
          role="button"
          tabIndex="0"
        >
          {hasIcon != "none" && (
            <LinkIcon
              icon={icon}
              styles={iconStyles}
              borderStyles={iconBorderStyles}
              classes={classes}
            />
          )}

          <LinkText element_id={element_id} />
        </div>
      </div>
    )
  } else if (url) {
    return (
      <div className={`relative ${hasIcon == "none" ? "justify-center" : ""}`}>
        <a
          href={url}
          className={`cursor-pointer flex items-center p-3 h-full no-underline transition duration-300 focus:outline-blue ${
            hasIcon === "none" ? "px-6 justify-center text-center" : ""
          }`}
          style={styles}
          target="_blank"
          rel="noreferrer noopener"
        >
          {hasIcon != "none" && (
            <LinkIcon
              icon={icon}
              styles={iconStyles}
              borderStyles={iconBorderStyles}
              classes={classes}
            />
          )}
          <LinkText element_id={element_id} />
        </a>
      </div>
    )
  } else {
    return null
  }
}

function LinkText({ element_id }) {
  const [element, updateElement] = useElement(element_id)
  const { text } = element
  const editing = useEditingContext()

  const handleChange = ({ content }) =>
    updateElement({ name: element_id, text: content })

  if (editing) {
    return (
      <ContentEditable
        tag="span"
        content={text}
        onChange={handleChange}
        editing={editing}
      />
    )
  } else if (text) {
    return <span>{text}</span>
  } else {
    return null
  }
}

function LinkInput({ settings: { radius, shadow }, addLink, closeInput }) {
  const [link, setLink] = useState("")
  const buttonStyles = {
    borderRadius: radius,
    boxShadow: shadow
  }

  return (
    <div className="text-center relative">
      <div
        className={`bg-white cursor-pointer flex-col p-3 h-full transition duration-300 focus:outline-blue`}
        style={buttonStyles}
      >
        <VisuallyHidden>
          <label htmlFor="link">Link</label>
        </VisuallyHidden>
        <Input
          name="link"
          className="flex"
          type="text"
          placeholder="Link URL"
          onChange={event => setLink(event.target.value)}
          tabIndex="0"
        />
        <div className="flex flex-row">
          <OldButton
            className="flex mt-2"
            onClick={() => addLink(link)}
            is="primary"
            type="submit"
          >
            Add link
          </OldButton>
          <OldButton
            className="flex mt-2 ml-2"
            onClick={closeInput}
            is="secondary"
            type="submit"
          >
            Cancel
          </OldButton>
        </div>
      </div>
    </div>
  )
}

function LinkInserter({
  settings: {
    backgroundColor,
    radius,
    shadow,
    textColor,
    linkBorder,
    linkBorderColor
  },
  showInput
}) {
  const styles = {
    backgroundColor: backgroundColor,
    borderRadius: radius,
    boxShadow: shadow,
    color: textColor,
    borderStyle: "solid",
    borderWidth: linkBorder,
    borderColor: linkBorderColor,
    minHeight: "83px"
  }
  const iconStyles = {
    width: 20,
    height: 20,
    margin: "20px 10px"
  }

  return (
    <div className="text-center relative">
      <button
        className={`bg-white cursor-pointer w-full flex items-center justify-center p-3 text-gray-800 transition duration-300 text-base focus:outline-blue`}
        style={styles}
        onClick={showInput}
      >
        <Icon name="plus-circle" style={iconStyles} />
        Add link
      </button>
    </div>
  )
}

function LinkCollection({
  links,
  settings,
  addLink,
  showLinkInput,
  showInput,
  closeInput,
  style
}) {
  const editing = useEditingContext()

  const { columns } = settings

  return (
    <>
      <div className={`grid gap-8 ${COLUMN_CLASSES[columns]}`} style={style}>
        {links.map(link => (
          <Link key={link.element_id} {...link} settings={settings} />
        ))}

        {editing &&
          (showLinkInput || isEmpty(links) ? (
            <LinkInput
              settings={settings}
              addLink={addLink}
              closeInput={closeInput}
            />
          ) : (
            <LinkInserter
              settings={settings}
              addLink={addLink}
              showInput={showInput}
            />
          ))}
      </div>
    </>
  )
}

export function LinksContainer({ element_id, ...props }) {
  const {
    children: links,
    addChild,
    removeChild
  } = useChildElements("parent_element_id", element_id)
  const { editing } = props
  const { setActiveElement } = useBuilderState()
  const [showLinkInput, setShowLinkInput] = useState(false)

  const addLink = async link => {
    let meta = await getLinkMeta(link)
    const newLink = addChild({
      type: "Link",
      url: meta.url,
      text: meta.title,
      icon: meta.icon,
      hasIcon: "social",
      iconColor: "#65dc8d"
    })
    setActiveElement(newLink[0].element_id)
    setShowLinkInput(false)
  }

  const styles = {
    columns: props.columns,
    color: props.textColor,
    fontSize: `${props.fontSize}px`,
    fontWeight: props.fontWeight,
    radius: props.radius,
    shadow: props.shadow
  }

  const showInput = () => {
    setShowLinkInput(true)
  }

  const closeInput = () => {
    if (isEmpty(links)) {
      removeChild(element_id)
    }
    setShowLinkInput(false)
  }

  const linksProps = {
    links,
    settings: props,
    style: styles,
    addLink,
    showLinkInput,
    showInput,
    closeInput
  }

  return (
    <div className="my-4 formkit-links">
      {editing && (
        <div className="mb-4">
          <SettingsButton onClick={props.onClick} />
        </div>
      )}

      <LinkCollection {...linksProps} />
    </div>
  )
}

LinksContainer.componentType = "Links"
